import { useEffect, useMemo, useRef, useState } from "react";import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DAYS_FR, addDays, mondayOf, toISODate, computeDayMinutes, minutesToHM, isoWeekLabel } from "@/lib/time";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Send, FileSpreadsheet, FileText, CheckCircle2, XCircle, Clock, PlayCircle, Coffee, UtensilsCrossed, StopCircle, UserCircle2, CalendarPlus, Wallet, GraduationCap, CalendarClock, RotateCcw, Loader2, AlertTriangle, Car, Umbrella } from "lucide-react";
import { buildGoogleCalendarUrl, openCalendarUrls } from "@/lib/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-muted text-muted-foreground" },
  submitted: { label: "En attente de validation", cls: "bg-primary/10 text-primary border border-primary/30" },
  approved: { label: "Validée", cls: "bg-secondary text-secondary-foreground border border-secondary" },
  rejected: { label: "Refusée", cls: "bg-destructive/10 text-destructive border border-destructive/30" },
};

// Types d'absence disponibles dans le dialogue unifié
const ABSENCE_TYPES = [
  { v: "maladie", label: "Maladie", flag: "sick", weekdayOnly: false, gcalTitle: "Maladie", gcalDescription: "Journée maladie (arrêt)." },
  { v: "conges", label: "Congés payés", flag: "paid_leave", weekdayOnly: false, gcalTitle: "Congés payés", gcalDescription: "Journée de congés payés." },
  { v: "recuperation", label: "Récupération", flag: "recovery", weekdayOnly: false, gcalTitle: "Récupération", gcalDescription: "Journée de récupération (7h forfait). Doit être validée par le responsable sur Axonaut." },
  { v: "ecole", label: "École (alternance)", flag: "school", weekdayOnly: true, gcalTitle: "École (alternance)", gcalDescription: "Journée à l'école (9h-12h / 13h-17h)." },
];

// Petit badge live qui affiche l'heure actuelle en Europe/Paris.
// Tick chaque 20 s. Rassure les employés en déplacement qu'ils pointent
// bien sur l'heure de l'agence, indépendamment du fuseau de leur appareil.
function ParisTimeBadge() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(t);
  }, []);
  const hhmm = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary border border-primary/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      title="Le pointage utilise l'heure de l'agence (Europe/Paris), pas celle de votre appareil."
      data-testid="paris-time-badge"
    >
      <Clock className="h-3 w-3" /> {hhmm} · Paris
    </span>
  );
}



function DayRow({ dayIdx, dateISO, entry, onChange, disabled, manualLocked, isToday, userEmail }) {
  const minutes = computeDayMinutes(entry || {});
  const isRecovery = !!(entry && entry.recovery);
  const isSchool = !!(entry && entry.school);
  const isSick = !!(entry && entry.sick);
  const isPaidLeave = !!(entry && entry.paid_leave);
  const isAbsent = isRecovery || isSchool || isSick || isPaidLeave;
  const absenceLabel = isRecovery ? "RÉCUPÉRATION" : isSchool ? "ÉCOLE" : isSick ? "MALADIE" : isPaidLeave ? "CONGÉS PAYÉS" : "";
  const absenceCls = isRecovery
    ? "bg-secondary text-secondary-foreground"
    : isSchool
      ? "bg-primary text-primary-foreground"
      : isSick
        ? "bg-destructive/80 text-destructive-foreground"
        : "bg-accent text-accent-foreground";
  const absenceHint = isRecovery
    ? "Journée bloquée · à valider par le responsable via Axonaut"
    : isSchool
      ? "9h-12h / 13h-17h · alternance"
      : isSick
        ? "Arrêt maladie · joindre un justificatif"
        : "Congés payés";
  const fields = [
    { k: "arrival", label: "Arrivée" },
    { k: "lunch_start", label: "Début pause" },
    { k: "lunch_end", label: "Fin pause" },
    { k: "departure", label: "Départ" },
  ];
  const addToCalendar = () => {
    let opts;
    if (isSchool) {
      opts = { title: "École (alternance) — Agence Antoine Événements", allDay: false, startDate: dateISO, startTime: "09:00", endTime: "17:00", description: "Journée à l'école (9h-12h / 13h-17h).", authUserEmail: userEmail || "" };
    } else if (isSick) {
      opts = { title: "Maladie — Agence Antoine Événements", allDay: true, startDate: dateISO, description: "Journée maladie (arrêt).", authUserEmail: userEmail || "" };
    } else if (isPaidLeave) {
      opts = { title: "Congés payés — Agence Antoine Événements", allDay: true, startDate: dateISO, description: "Journée de congés payés.", authUserEmail: userEmail || "" };
    } else {
      opts = { title: "Récupération — Agence Antoine Événements", allDay: true, startDate: dateISO, description: "Journée de récupération (7h forfait). Doit être validée par le responsable sur Axonaut.", authUserEmail: userEmail || "" };
    }
    openCalendarUrls([buildGoogleCalendarUrl(opts)]);
  };
  return (
    <div className={`fade-up grid grid-cols-12 gap-2 md:gap-3 items-center border-b border-border/60 py-3 ${isAbsent ? "bg-muted rounded-lg opacity-90" : isToday ? "bg-primary/5 rounded-lg" : ""}`}>
      <div className="col-span-12 md:col-span-2">
        <div className="font-display font-medium flex items-center gap-1 flex-wrap">
          {DAYS_FR[dayIdx]}
          {isToday && !isAbsent && <span className="text-[9px] uppercase tracking-widest text-primary font-bold bg-primary/10 px-1.5 rounded-full">auj.</span>}
        </div>
        <div className="text-xs text-muted-foreground">{dateISO}</div>
      </div>
      {isAbsent ? (
        <div className="col-span-12 md:col-span-8 flex flex-wrap items-center gap-2 md:gap-3 px-2">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-brand font-bold tracking-widest text-sm ${absenceCls}`} data-testid={`day-${dayIdx}-absence-badge`}>
            {absenceLabel}
          </span>
          <span className="text-xs text-muted-foreground italic">{absenceHint}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full h-7 text-xs ml-auto"
            onClick={addToCalendar}
            data-testid={`day-${dayIdx}-add-to-gcal`}
            title="Ajouter à Google Agenda"
          >
            <CalendarClock className="h-3.5 w-3.5 mr-1" /> Google Agenda
          </Button>
        </div>
      ) : fields.map((f) => (
        <div key={f.k} className="col-span-6 md:col-span-2 time-cell">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{f.label}</div>
          <Input
            type="time"
            value={entry?.[f.k] || ""}
            disabled={disabled || manualLocked}
            readOnly={manualLocked}
            onChange={(e) => onChange({ ...(entry || { date: dateISO }), date: dateISO, [f.k]: e.target.value })}
            data-testid={`day-${dayIdx}-${f.k}`}
            className={`h-10 bg-muted ${manualLocked ? "cursor-not-allowed" : ""}`}
          />
        </div>
      ))}
      <div className="col-span-12 md:col-span-2 text-right">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Heures</div>
        <div className={`font-display text-xl font-semibold tabular-nums ${(isRecovery || isSchool) ? "text-primary" : ""}`} data-testid={`day-${dayIdx}-hours`}>
          {minutesToHM(minutes)}
        </div>
      </div>
    </div>
  );
}

export default function WeekPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const weekStart = toISODate(monday);
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  // Snapshot JSON du dernier état sauvegardé — permet à l'auto-save
  // de ne se déclencher que sur un vrai changement utilisateur (et pas
  // sur le premier render). `null` = pas encore chargé, on skip.
  const savedSnapshotRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [viewUserId, setViewUserId] = useState("");
  // Dialogue absence unifié (Maladie / Congés payés / Récupération / École)
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [absenceType, setAbsenceType] = useState(""); // "maladie" | "conges" | "recuperation" | "ecole"
  const [absenceStart, setAbsenceStart] = useState("");
  const [absenceEnd, setAbsenceEnd] = useState("");
  const [recoveryBalance, setRecoveryBalance] = useState(null);
  const [offSite, setOffSite] = useState(false);
  // Dialogue temps de trajet
  const [commuteOpen, setCommuteOpen] = useState(false);
  const [commuteDate, setCommuteDate] = useState("");
  const [commuteHours, setCommuteHours] = useState("");
  const [commuteMinutes, setCommuteMinutes] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetScope, setResetScope] = useState("week"); // "week" | "month" | "year"
  const [resetBusy, setResetBusy] = useState(false);
  const isAlternant = user?.contract_status === "Alternant";
  const targetUserId = viewUserId || user?.id;
  const viewingOther = !!viewUserId && viewUserId !== user?.id;
  const todayISO = toISODate(new Date());

  const load = async () => {
    setLoading(true);
    try {
      const params = { week_start: weekStart };
      if (viewingOther) params.user_id = viewUserId;
      const { data: d } = await api.get("/timesheets/week", { params });
      setData(d);
      const map = {};
      (d.timesheet.entries || []).forEach((e) => { map[e.date] = e; });
      const arr = Array.from({ length: 7 }).map((_, i) => {
        const iso = toISODate(addDays(monday, i));
        return map[iso] || { date: iso };
      });
      setEntries(arr);
      savedSnapshotRef.current = JSON.stringify(arr);
      setSavedAt(null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      api.get("/users/lookup").then(({ data }) => setTeamUsers(data)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => { load(); loadBalance(); /* eslint-disable-next-line */ }, [weekStart, viewUserId]);

  const loadBalance = async () => {
    try {
      const params = viewingOther ? { user_id: viewUserId } : {};
      const { data: b } = await api.get("/timesheets/recovery-balance", { params });
      setRecoveryBalance(b);
    } catch (e) { /* silent */ }
  };

  const submitAbsence = async () => {
    const meta = ABSENCE_TYPES.find(t => t.v === absenceType);
    if (!meta) { toast.error("Sélectionnez un type d'absence"); return; }
    if (meta.v === "ecole" && !isAlternant && !isAdmin) { toast.error("L'école est réservée aux alternants"); return; }
    if (!absenceStart) { toast.error("Sélectionnez une date de début"); return; }
    const end = absenceEnd || absenceStart;
    if (end < absenceStart) { toast.error("La date de fin doit être ≥ à la date de début"); return; }
    // Énumère la plage (max 90 jours) — filtre lundi→vendredi si l'école
    const dates = [];
    let cur = new Date(absenceStart + "T00:00:00");
    const stop = new Date(end + "T00:00:00");
    let guard = 0;
    while (cur <= stop && guard++ < 90) {
      const dow = cur.getDay();
      if (!meta.weekdayOnly || (dow >= 1 && dow <= 5)) dates.push(toISODate(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    if (dates.length === 0) { toast.error(meta.weekdayOnly ? "Aucun jour de semaine sélectionné" : "Plage vide"); return; }
    // Vérif solde récupération
    if (meta.v === "recuperation") {
      const needed = dates.length * 7;
      if (recoveryBalance && recoveryBalance.balance_hours < needed) {
        if (!confirm(`Solde insuffisant : ${recoveryBalance.balance_hours.toFixed(2)}h dispo pour ${needed}h demandées. Continuer quand même ?`)) return;
      }
    }
    // Grouper par lundi
    const byWeek = {};
    for (const iso of dates) {
      const wStart = toISODate(mondayOf(new Date(iso + "T00:00:00")));
      (byWeek[wStart] = byWeek[wStart] || []).push(iso);
    }
    try {
      for (const [wStart, isoList] of Object.entries(byWeek)) {
        let existing = [];
        if (wStart === weekStart) {
          existing = entries.filter(e => !isoList.includes(e.date));
        } else {
          const { data: other } = await api.get("/timesheets/week", { params: { week_start: wStart } });
          existing = (other.timesheet.entries || []).filter(e => !isoList.includes(e.date));
        }
        const newRows = isoList.map(iso => {
          const row = { date: iso, [meta.flag]: true };
          if (meta.v === "ecole") { row.arrival = "09:00"; row.lunch_start = "12:00"; row.lunch_end = "13:00"; row.departure = "17:00"; }
          return row;
        });
        const merged = [...existing, ...newRows];
        await api.post("/timesheets/week", { week_start: wStart, entries: merged });
      }
      toast.success(`${dates.length} jour(s) — ${meta.label} enregistré(s)`);
      if (!viewingOther) {
        const urls = dates.map(iso => buildGoogleCalendarUrl({
          title: `${meta.gcalTitle} — Agence Antoine Événements`,
          allDay: meta.v !== "ecole",
          startDate: iso,
          ...(meta.v === "ecole" ? { startTime: "09:00", endTime: "17:00" } : {}),
          description: meta.gcalDescription,
          authUserEmail: user?.email || "",
        }));
        openCalendarUrls(urls);
      }
      setAbsenceOpen(false);
      setAbsenceType("");
      setAbsenceStart("");
      setAbsenceEnd("");
      load();
      loadBalance();
    } catch (e) { toast.error(extractError(e)); }
  };

  const submitCommute = async () => {
    if (!commuteDate) { toast.error("Sélectionnez une date"); return; }
    const h = parseInt(commuteHours || 0);
    const m = parseInt(commuteMinutes || 0);
    const total = (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
    if (total <= 0) { toast.error("Renseignez une durée > 0"); return; }
    try {
      const body = { date: commuteDate, minutes: total };
      if (viewingOther) body.user_id = viewUserId;
      await api.post("/timesheets/commute", body);
      toast.success(`Trajet enregistré : ${Math.floor(total/60)}h${String(total%60).padStart(2,"0")}`);
      setCommuteOpen(false);
      setCommuteDate("");
      setCommuteHours("");
      setCommuteMinutes("");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const totalMin = useMemo(() => entries.reduce((s, e) => s + computeDayMinutes(e), 0), [entries]);
  const totalHours = totalMin / 60;
  const delta = totalHours - 35;
  const status = data?.timesheet?.status || "draft";
  // Une feuille est verrouillée UNIQUEMENT quand un admin l'a validée.
  // Avant validation (draft / submitted / rejected), l'employé peut
  // toujours modifier ses horaires et son commentaire — l'auto-save
  // enregistre au fil de l'eau.
  const disabled = viewingOther ? false : (status === "approved");
  const manualLockedForUser = !isAdmin && !viewingOther;

  const submitReset = async () => {
    if (!viewingOther) return;
    let reference = "";
    let label = "";
    if (resetScope === "week") { reference = weekStart; label = `la semaine du ${weekStart}`; }
    else if (resetScope === "month") { reference = weekStart.slice(0, 7); label = `le mois ${reference}`; }
    else { reference = weekStart.slice(0, 4); label = `l'année ${reference}`; }
    if (!window.confirm(`Supprimer définitivement toutes les données de ${label} pour ${data?.user?.name || data?.user?.email} ?\n\nCette action est irréversible.`)) return;
    setResetBusy(true);
    try {
      const { data: r } = await api.post("/timesheets/reset", { user_id: viewUserId, scope: resetScope, reference });
      toast.success(`${r.deleted} feuille(s) supprimée(s)`);
      setResetOpen(false);
      load();
      loadBalance();
    } catch (e) { toast.error(extractError(e)); }
    finally { setResetBusy(false); }
  };

  const save = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      const filtered = entries.filter((e) => e.arrival || e.lunch_start || e.lunch_end || e.departure || e.note);
      const payload = { week_start: weekStart, entries: filtered };
      if (viewingOther) payload.user_id = viewUserId;
      await api.post("/timesheets/week", payload);
      savedSnapshotRef.current = JSON.stringify(entries);
      setSavedAt(new Date());
      if (!silent) toast.success("Semaine enregistrée");
      // Recharger la version canonique sans écran de chargement (silencieux)
      if (!silent) load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save : dès qu'une entrée (horaires ou commentaire) change,
  // on attend 1,2 s d'inactivité puis on sauvegarde silencieusement.
  // Désactivé pendant `saving`, `loading`, et quand la semaine est verrouillée.
  useEffect(() => {
    if (loading || saving || disabled) return;
    if (savedSnapshotRef.current === null) return;
    const snap = JSON.stringify(entries);
    if (snap === savedSnapshotRef.current) return;
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { save({ silent: true }); }, 1200);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, loading, saving, disabled]);

  const clockAction = async (field) => {
    // Géolocalisation via navigateur (non bloquant : si refusé/indisponible,
    // on envoie le pointage quand même avec la raison de l'échec).
    const getGeo = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ error: "API géolocalisation indisponible" });
      const timer = setTimeout(() => resolve({ error: "Timeout (position non reçue en 8s)" }), 8000);
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(timer); resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }); },
        (err) => { clearTimeout(timer); resolve({ error: err.message || "Refusé" }); },
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 0 }
      );
    });
    const geo = await getGeo();
    try {
      const { data } = await api.post("/timesheets/clock", {
        field,
        latitude: geo.latitude ?? null,
        longitude: geo.longitude ?? null,
        accuracy: geo.accuracy ?? null,
        offsite: !!offSite,
        geo_error: geo.error || null,
      });
      toast.success(`Pointé à ${data.time}`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submit = async () => {
    try {
      const filtered = entries.filter((e) => e.arrival || e.lunch_start || e.lunch_end || e.departure || e.note);
      await api.post("/timesheets/week", { week_start: weekStart, entries: filtered });
      await api.post("/timesheets/submit", { user_id: user.id, week_start: weekStart });
      toast.success("Semaine envoyée pour validation");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const downloadExport = async (kind) => {
    try {
      const res = await api.get(`/timesheets/export/${kind}`, {
        params: { week_start: weekStart }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `releve_${weekStart}.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const st = STATUS[status] || STATUS.draft;

  return (
    <div className="space-y-6" data-testid="week-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Semaine</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {isoWeekLabel(weekStart)}
          </h1>
          <div className="text-sm text-muted-foreground mt-1">
            Du {weekStart} au {toISODate(addDays(monday, 6))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Select value={viewUserId || "me"} onValueChange={v => setViewUserId(v === "me" ? "" : v)}>
              <SelectTrigger className="min-w-[220px]" data-testid="admin-user-select">
                <UserCircle2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Voir la semaine de..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Ma semaine</SelectItem>
                {teamUsers.filter(u => u.id !== user?.id).map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonday(addDays(monday, -7))} data-testid="prev-week-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setMonday(mondayOf(new Date()))} data-testid="this-week-btn">
            Cette semaine
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonday(addDays(monday, 7))} data-testid="next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
            onClick={() => { setCommuteDate(todayISO); setCommuteHours(""); setCommuteMinutes(""); setCommuteOpen(true); }}
            data-testid="declare-commute-btn"
            title="Déclarer votre temps de trajet pour venir à l'agence"
          >
            <Car className="h-4 w-4 mr-2" /> Temps de trajet
          </Button>
        </div>
      </header>

      {viewingOther && data?.user && (
        <div className="bg-accent/40 border border-accent rounded-2xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap text-sm" data-testid="viewing-user-banner">
          <div className="flex items-center gap-2">
            <UserCircle2 className="h-4 w-4" /> Vue admin — semaine de <strong>{data.user.name || data.user.email}</strong>.
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setResetOpen(true)}
              data-testid="admin-reset-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Réinitialiser…
            </Button>
          )}
        </div>
      )}

      {!viewingOther && (
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Pointage du jour</div>
                  <ParisTimeBadge />
                </div>
                <div className="text-xs text-muted-foreground">{todayISO} — un clic remplit l'heure exacte, non modifiable ensuite.</div>
                <label className="flex items-center gap-2 mt-2 cursor-pointer select-none" data-testid="offsite-toggle">
                  <input
                    type="checkbox"
                    checked={offSite}
                    onChange={e => setOffSite(e.target.checked)}
                    className="h-4 w-4 rounded border-border accent-primary"
                    data-testid="offsite-checkbox"
                  />
                  <span className="text-xs">Je suis en dehors de l'agence lors de mon pointage</span>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full bg-primary text-primary-foreground active:scale-95" onClick={() => clockAction("arrival")} data-testid="clock-arrival" disabled={disabled}>
                  <PlayCircle className="h-4 w-4 mr-2" /> Commencer ma journée
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => clockAction("lunch_start")} data-testid="clock-lunch-start" disabled={disabled}>
                  <UtensilsCrossed className="h-4 w-4 mr-2" /> Commencer ma pause déjeuner
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => clockAction("lunch_end")} data-testid="clock-lunch-end" disabled={disabled}>
                  <Coffee className="h-4 w-4 mr-2" /> Terminer ma pause déjeuner
                </Button>
                <Button className="rounded-full bg-secondary text-secondary-foreground active:scale-95" onClick={() => clockAction("departure")} data-testid="clock-departure" disabled={disabled}>
                  <StopCircle className="h-4 w-4 mr-2" /> Terminer ma journée
                </Button>
                <Button variant="outline" className="rounded-full border-accent bg-accent/40 hover:bg-accent" onClick={() => { setAbsenceType(""); setAbsenceStart(""); setAbsenceEnd(""); setAbsenceOpen(true); }} data-testid="declare-absence-btn" disabled={disabled}>
                  <Umbrella className="h-4 w-4 mr-2" /> Déclarer une absence
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Statut</div>
            <div className="mt-2">
              <Badge className={`${st.cls} px-3 py-1 rounded-full`} data-testid="week-status-badge">{st.label}</Badge>
            </div>
            {status === "approved" && (
              <div className="text-xs text-primary mt-2 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Semaine validée</div>
            )}
            {data?.timesheet?.review_comment && (
              <p className="text-xs text-muted-foreground mt-3 italic">« {data.timesheet.review_comment} »</p>
            )}
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total effectué</div>
            <div className="font-display text-3xl font-semibold tabular-nums mt-1" data-testid="week-total-hours">
              {minutesToHM(totalMin)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">sur 35h00</div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Solde récupération de la semaine</div>
            <div className={`font-display text-3xl font-semibold tabular-nums mt-1 ${delta >= 0 ? "text-primary" : "text-destructive"}`} data-testid="week-delta">
              {delta >= 0 ? "+" : ""}{delta.toFixed(2)} h
            </div>
            <div className="text-xs text-muted-foreground mt-1">différentiel semaine</div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm bg-accent/10 border-accent/40">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Wallet className="h-3 w-3" /> Solde récup total</div>
            <div className="font-display text-3xl font-semibold tabular-nums mt-1" data-testid="recovery-balance">
              {recoveryBalance ? `${recoveryBalance.balance_hours >= 0 ? "+" : ""}${recoveryBalance.balance_hours.toFixed(2)} h` : "—"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {recoveryBalance ? `Acquis : +${recoveryBalance.earned_hours.toFixed(1)}h · Réservés : −${recoveryBalance.held_hours.toFixed(1)}h` : "cumul semaines validées"}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-5 flex flex-col gap-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Exports</div>
            <Button variant="outline" size="sm" className="rounded-full justify-start" onClick={() => downloadExport("excel")} data-testid="export-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="rounded-full justify-start" onClick={() => downloadExport("pdf")} data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-2" /> PDF
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-4 md:p-6">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Clock className="h-4 w-4 animate-pulse" /> Chargement...
            </div>
          ) : (
            <div>
              {entries.map((e, i) => (
                <DayRow
                  key={e.date}
                  dayIdx={i}
                  dateISO={e.date}
                  entry={e}
                  disabled={disabled}
                  manualLocked={manualLockedForUser}
                  isToday={e.date === todayISO}
                  userEmail={viewingOther ? (data?.user?.email || "") : (user?.email || "")}
                  onChange={(next) => {
                    const arr = [...entries];
                    arr[i] = next;
                    setEntries(arr);
                  }}
                />
              ))}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Textarea
                  placeholder="Notes / commentaires pour la semaine (optionnel)"
                  value={entries[0]?.note || ""}
                  disabled={disabled}
                  onChange={(e) => {
                    const arr = [...entries];
                    arr[0] = { ...(arr[0] || { date: toISODate(monday) }), note: e.target.value };
                    setEntries(arr);
                  }}
                  className="bg-muted"
                  data-testid="week-note"
                />
                <div className="flex items-center justify-end gap-2 flex-wrap">
                  {savedAt && !saving && (
                    <span className="text-xs text-primary flex items-center gap-1" data-testid="week-saved-indicator">
                      <CheckCircle2 className="h-3 w-3" /> Enregistré à {savedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                  {saving && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3 animate-pulse" /> Enregistrement…
                    </span>
                  )}
                  <Button variant="outline" className="rounded-full" onClick={() => save()} disabled={saving || disabled} data-testid="save-week-btn">
                    <Save className="h-4 w-4 mr-2" /> Enregistrer
                  </Button>
                  {status !== "approved" && (
                    <Button className="rounded-full active:scale-95" onClick={submit} disabled={disabled && status !== "rejected"} data-testid="submit-week-btn">
                      <Send className="h-4 w-4 mr-2" /> Envoyer pour validation
                    </Button>
                  )}
                  {status === "approved" && (
                    <div className="text-sm flex items-center gap-1.5 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4" /> Semaine validée
                    </div>
                  )}
                  {status === "rejected" && (
                    <div className="text-sm flex items-center gap-1.5 text-destructive">
                      <XCircle className="h-4 w-4" /> Corrections requises
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={absenceOpen} onOpenChange={setAbsenceOpen}>
        <DialogContent className="max-w-lg" data-testid="absence-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Umbrella className="h-5 w-5 text-primary" /> Déclarer une absence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type d'absence</Label>
              <Select value={absenceType} onValueChange={setAbsenceType}>
                <SelectTrigger data-testid="absence-type"><SelectValue placeholder="Sélectionner un type…" /></SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.filter(t => t.v !== "ecole" || isAlternant || isAdmin).map(t => (
                    <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isAlternant && !isAdmin && (
                <div className="text-[11px] text-muted-foreground mt-1 italic">L'école (alternance) est réservée aux alternants.</div>
              )}
            </div>
            {absenceType === "recuperation" && (
              <div className="bg-accent/40 border border-accent rounded-lg p-3 text-sm">
                <strong>⚠️ Cette récupération doit avoir été demandée à votre responsable sur Axonaut et validée en amont.</strong> Sans cette validation préalable, elle pourra être refusée lors de la validation hebdomadaire.
              </div>
            )}
            {absenceType === "maladie" && (
              <div className="bg-destructive/10 border border-destructive/40 rounded-lg p-3 text-sm">
                📄 N'oubliez pas de transmettre votre <strong>arrêt de travail</strong> à l'AAE dans les 48h.
              </div>
            )}
            {absenceType === "ecole" && (
              <div className="bg-primary/10 border border-primary/40 rounded-lg p-3 text-sm">
                Journées marquées <strong>ÉCOLE</strong> avec l'horaire automatique <strong>9h-12h / 13h-17h</strong>. Seuls les lundis→vendredis seront pris en compte.
              </div>
            )}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted border border-border/60 rounded-lg p-2">
              <CalendarClock className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>Après enregistrement, un onglet <strong>Google Agenda</strong> s'ouvrira pour chaque jour (compte : <strong>{user?.email}</strong>).</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Du</Label>
                <Input type="date" value={absenceStart} onChange={e => setAbsenceStart(e.target.value)} data-testid="absence-start" />
              </div>
              <div>
                <Label>Au</Label>
                <Input type="date" value={absenceEnd} min={absenceStart} onChange={e => setAbsenceEnd(e.target.value)} data-testid="absence-end" />
                <div className="text-[11px] text-muted-foreground mt-1">Vide = 1 seule journée.</div>
              </div>
            </div>
            {(() => {
              if (!absenceType || !absenceStart) return null;
              const meta = ABSENCE_TYPES.find(t => t.v === absenceType);
              const end = absenceEnd || absenceStart;
              if (end < absenceStart) return <div className="text-xs text-destructive">La date de fin doit être postérieure ou égale à la date de début.</div>;
              const d1 = new Date(absenceStart + "T00:00:00");
              const d2 = new Date(end + "T00:00:00");
              let days = 0;
              for (let d = new Date(d1); d <= d2; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
                const dow = d.getDay();
                if (!meta.weekdayOnly || (dow >= 1 && dow <= 5)) days++;
              }
              const hours = days * 7;
              const showBalance = meta.v === "recuperation" && recoveryBalance;
              const enough = !showBalance || recoveryBalance.balance_hours >= hours;
              return (
                <div className="border border-border/60 rounded-lg p-3 bg-muted text-sm space-y-1" data-testid="absence-summary">
                  <div><strong className="tabular-nums">{days}</strong> jour(s) — {meta.label}</div>
                  <div>Consommation prévue : <strong className="tabular-nums">{hours}h</strong> ({days} × 7h forfait)</div>
                  {showBalance && (
                    <div>
                      Solde récupération : <strong className={enough ? "text-primary" : "text-destructive"}>{recoveryBalance.balance_hours.toFixed(2)} h</strong>
                      {!enough && <span className="text-destructive"> — insuffisant, contactez votre responsable.</span>}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAbsenceOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-primary active:scale-95" onClick={submitAbsence} data-testid="absence-confirm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={commuteOpen} onOpenChange={setCommuteOpen}>
        <DialogContent className="max-w-md" data-testid="commute-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-primary" /> Temps de trajet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Déclarez le temps qu'il vous a fallu pour venir à l'agence ce jour-là.
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={commuteDate} onChange={e => setCommuteDate(e.target.value)} data-testid="commute-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Heures</Label>
                <Input type="number" min="0" max="12" value={commuteHours} onChange={e => setCommuteHours(e.target.value)} placeholder="0" data-testid="commute-hours" />
              </div>
              <div>
                <Label>Minutes</Label>
                <Input type="number" min="0" max="59" value={commuteMinutes} onChange={e => setCommuteMinutes(e.target.value)} placeholder="30" data-testid="commute-minutes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCommuteOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-primary active:scale-95" onClick={submitCommute} data-testid="commute-confirm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== Reset admin dialog ==== */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Réinitialiser les données de temps
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-destructive/5 border border-destructive/30 rounded-lg p-3 text-destructive text-xs">
              Cette action supprime <strong>toutes les feuilles</strong> concernées de {data?.user?.name || data?.user?.email}. Le solde de récupération sera recalculé automatiquement. <strong>Irréversible.</strong>
            </div>
            <div>
              <Label>Périmètre à réinitialiser</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { v: "week",  lbl: "Cette semaine",   hint: weekStart },
                  { v: "month", lbl: "Ce mois",         hint: weekStart.slice(0, 7) },
                  { v: "year",  lbl: "Cette année",     hint: weekStart.slice(0, 4) },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setResetScope(o.v)}
                    className={`rounded-xl border px-3 py-3 text-left transition ${resetScope === o.v ? "border-destructive bg-destructive/10 text-destructive" : "border-border hover:bg-muted"}`}
                    data-testid={`reset-scope-${o.v}`}
                  >
                    <div className="text-sm font-semibold">{o.lbl}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setResetOpen(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-95"
              onClick={submitReset}
              disabled={resetBusy}
              data-testid="reset-confirm"
            >
              {resetBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
